import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  DollarSign,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  Shield,
  Settings,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Users,
  Zap,
  History,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditClientPage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Client data
  const [client, setClient] = useState(null);
  const [clientSettings, setClientSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  // Global pricing for reference
  const [globalPricing, setGlobalPricing] = useState({
    cold_call_price: 5.00,
    live_transfer_price: 25.00,
    data_lead_price: 2.00
  });
  
  // Top-up form
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNotes, setTopUpNotes] = useState('');
  const [addingBalance, setAddingBalance] = useState(false);
  
  // Collapsible sections
  const [contactOpen, setContactOpen] = useState(true);
  const [billingOpen, setBillingOpen] = useState(true);

  useEffect(() => {
    fetchClientData();
    fetchGlobalPricing();
  }, [clientId]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dme_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch client details
      const clientRes = await axios.get(`${API_URL}/api/admin/clients/${clientId}`, { headers });
      setClient(clientRes.data);
      
      // Fetch client settings
      const settingsRes = await axios.get(`${API_URL}/api/admin/clients/${clientId}/settings`, { headers });
      setClientSettings(settingsRes.data);
      
      // Fetch transactions
      try {
        const txRes = await axios.get(`${API_URL}/api/admin/clients/${clientId}/transactions`, { headers });
        setTransactions(txRes.data || []);
      } catch (e) {
        setTransactions([]);
      }
    } catch (error) {
      toast.error('Failed to load client data');
      navigate('/client-management');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalPricing = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const response = await axios.get(`${API_URL}/api/admin/clients/pricing/global`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch global pricing');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(`${API_URL}/api/admin/clients/${clientId}/settings`, clientSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const newStatus = client.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${API_URL}/api/admin/clients/${clientId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient({ ...client, status: newStatus });
      toast.success(`Client ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddBalance = async () => {
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setAddingBalance(true);
    try {
      const token = localStorage.getItem('dme_token');
      await axios.post(`${API_URL}/api/admin/clients/${clientId}/balance/topup`, {
        amount: parseFloat(topUpAmount),
        payment_method: 'admin',
        notes: topUpNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Balance added successfully');
      setTopUpAmount('');
      setTopUpNotes('');
      fetchClientData(); // Refresh to get new balance
    } catch (error) {
      toast.error('Failed to add balance');
    } finally {
      setAddingBalance(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
      case 'suspended':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NC';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!client || !clientSettings) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <AlertCircle className="w-12 h-12 text-muted-foreground" />
        <p className="text-lg text-muted-foreground">Client not found</p>
        <Button onClick={() => navigate('/client-management')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950" data-testid="edit-client-page">
      {/* Left Sidebar - Client Info */}
      <div className="w-80 bg-white dark:bg-slate-900 border-r flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-3 -ml-2"
            onClick={() => navigate('/client-management')}
            data-testid="back-to-clients-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Clients
          </Button>
          
          {/* Client Avatar & Name */}
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xl font-semibold">
              {getInitials(client.company_name)}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">
                {client.company_name}
              </h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-3 h-3" />
                <span>{client.contact_name}</span>
              </div>
            </div>
          </div>
          
          {/* Status */}
          <div className="flex items-center gap-2 mt-3">
            {getStatusBadge(client.status)}
            {client.is_available ? (
              <Badge className="bg-green-500">Available</Badge>
            ) : (
              <Badge variant="outline">Unavailable</Badge>
            )}
          </div>
          
          {/* Toggle Status Button */}
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full mt-3"
            onClick={handleToggleStatus}
          >
            {client.status === 'active' ? 'Deactivate Client' : 'Activate Client'}
          </Button>
        </div>
        
        {/* Scrollable Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Contact Section */}
          <Collapsible open={contactOpen} onOpenChange={setContactOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                CONTACT
              </span>
              {contactOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{client.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{client.phone || 'No phone'}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Billing Section */}
          <Collapsible open={billingOpen} onOpenChange={setBillingOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                BILLING
              </span>
              {billingOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Current Balance</div>
                <p className="font-medium font-mono text-2xl text-green-600">
                  ${client.balance?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Total Leads Received</div>
                <p className="font-medium text-lg">{client.total_leads_received || 0}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Lead Types</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {clientSettings.receive_cold_calls && <Badge variant="outline" className="text-xs">Cold Calls</Badge>}
                  {clientSettings.receive_live_transfers && <Badge variant="outline" className="text-xs">Live Transfers</Badge>}
                  {clientSettings.receive_data_leads && <Badge variant="outline" className="text-xs">Data Leads</Badge>}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* API Access Section */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50">
              <span className="font-semibold text-sm flex items-center gap-2">
                <Zap className="w-4 h-4" />
                DIALER ACCESS
              </span>
              <ChevronRight className="w-4 h-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${clientSettings.convoso_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>Convoso</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${clientSettings.vichi_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>Vichi</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className={`w-2 h-2 rounded-full ${clientSettings.tld_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span>TLD</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        
        {/* Save Button at Bottom */}
        <div className="p-4 border-t bg-white dark:bg-slate-900">
          <Button 
            className="w-full" 
            onClick={handleSaveSettings} 
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Tabs */}
        <div className="bg-white dark:bg-slate-900 border-b px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 p-0 gap-1">
              <TabsTrigger 
                value="settings" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                Lead Settings
              </TabsTrigger>
              <TabsTrigger 
                value="dialer" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-dialer"
              >
                <Phone className="w-4 h-4 mr-2" />
                Dialer Access
              </TabsTrigger>
              <TabsTrigger 
                value="pricing" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-pricing"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Custom Pricing
              </TabsTrigger>
              <TabsTrigger 
                value="balance" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-balance"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Balance & History
              </TabsTrigger>
              <TabsTrigger 
                value="notes" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                data-testid="tab-notes"
              >
                <Users className="w-4 h-4 mr-2" />
                Admin Notes
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Lead Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-3xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Lead Type Preferences
                  </CardTitle>
                  <CardDescription>
                    Configure which types of leads this client should receive
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label className="text-base">Receive Cold Calls</Label>
                      <p className="text-sm text-muted-foreground">Client will receive cold call leads</p>
                    </div>
                    <Switch
                      checked={clientSettings.receive_cold_calls}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, receive_cold_calls: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label className="text-base">Receive Live Transfers</Label>
                      <p className="text-sm text-muted-foreground">Client will receive live transfer leads</p>
                    </div>
                    <Switch
                      checked={clientSettings.receive_live_transfers}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, receive_live_transfers: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label className="text-base">Receive Data Leads</Label>
                      <p className="text-sm text-muted-foreground">Client will receive data leads</p>
                    </div>
                    <Switch
                      checked={clientSettings.receive_data_leads}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, receive_data_leads: checked})}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Distribution Settings
                  </CardTitle>
                  <CardDescription>
                    Configure how leads are distributed to this client
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Distribution Mode</Label>
                      <Select
                        value={clientSettings.distribution_mode}
                        onValueChange={(value) => setClientSettings({...clientSettings, distribution_mode: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="round_robin">Round Robin</SelectItem>
                          <SelectItem value="type_matching">Type Matching</SelectItem>
                          <SelectItem value="manual">Manual Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority (lower = higher priority)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={clientSettings.distribution_priority}
                        onChange={(e) => setClientSettings({...clientSettings, distribution_priority: parseInt(e.target.value) || 100})}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Lead Limits
                  </CardTitle>
                  <CardDescription>
                    Set daily and monthly caps on lead volume
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Daily Lead Limit</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="No limit"
                        value={clientSettings.daily_lead_limit || ''}
                        onChange={(e) => setClientSettings({...clientSettings, daily_lead_limit: e.target.value ? parseInt(e.target.value) : null})}
                      />
                      <p className="text-xs text-muted-foreground">Leads today: {clientSettings.leads_today || 0}</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Monthly Lead Limit</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="No limit"
                        value={clientSettings.monthly_lead_limit || ''}
                        onChange={(e) => setClientSettings({...clientSettings, monthly_lead_limit: e.target.value ? parseInt(e.target.value) : null})}
                      />
                      <p className="text-xs text-muted-foreground">Leads this month: {clientSettings.leads_this_month || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Dialer Access Tab */}
          {activeTab === 'dialer' && (
            <div className="space-y-6 max-w-3xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Dialer API Access
                  </CardTitle>
                  <CardDescription>
                    Enable dialer integrations for this client. Once enabled, the client can configure their own API keys in their portal.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${clientSettings.convoso_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Zap className="w-5 h-5" />
                      </div>
                      <div>
                        <Label className="text-base">Convoso Dialer</Label>
                        <p className="text-sm text-muted-foreground">Enable Convoso API integration</p>
                      </div>
                    </div>
                    <Switch
                      checked={clientSettings.convoso_enabled}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, convoso_enabled: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${clientSettings.vichi_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Zap className="w-5 h-5" />
                      </div>
                      <div>
                        <Label className="text-base">Vichi Dialer</Label>
                        <p className="text-sm text-muted-foreground">Enable Vichi API integration</p>
                      </div>
                    </div>
                    <Switch
                      checked={clientSettings.vichi_enabled}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, vichi_enabled: checked})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${clientSettings.tld_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Zap className="w-5 h-5" />
                      </div>
                      <div>
                        <Label className="text-base">TLD Dialer</Label>
                        <p className="text-sm text-muted-foreground">Enable TLD API integration</p>
                      </div>
                    </div>
                    <Switch
                      checked={clientSettings.tld_enabled}
                      onCheckedChange={(checked) => setClientSettings({...clientSettings, tld_enabled: checked})}
                    />
                  </div>
                  
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200">API Keys Managed by Client</p>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          After you enable these integrations, the client will be able to enter their own API keys 
                          through their portal. You are only enabling access, not configuring credentials.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Custom Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6 max-w-3xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Custom Lead Pricing
                  </CardTitle>
                  <CardDescription>
                    Override the global pricing for this client. Leave empty to use global pricing.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Cold Call Price Override</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={`Global: $${globalPricing.cold_call_price}`}
                          value={clientSettings.cold_call_price_override || ''}
                          onChange={(e) => setClientSettings({...clientSettings, cold_call_price_override: e.target.value ? parseFloat(e.target.value) : null})}
                          className="pl-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Live Transfer Price Override</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={`Global: $${globalPricing.live_transfer_price}`}
                          value={clientSettings.live_transfer_price_override || ''}
                          onChange={(e) => setClientSettings({...clientSettings, live_transfer_price_override: e.target.value ? parseFloat(e.target.value) : null})}
                          className="pl-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Data Lead Price Override</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={`Global: $${globalPricing.data_lead_price}`}
                          value={clientSettings.data_lead_price_override || ''}
                          onChange={(e) => setClientSettings({...clientSettings, data_lead_price_override: e.target.value ? parseFloat(e.target.value) : null})}
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800 dark:text-blue-200">Current Effective Pricing</p>
                        <div className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                          <p>Cold Call: ${clientSettings.cold_call_price?.toFixed(2) || globalPricing.cold_call_price.toFixed(2)} {clientSettings.using_custom_pricing && clientSettings.cold_call_price_override && <span className="text-xs">(custom)</span>}</p>
                          <p>Live Transfer: ${clientSettings.live_transfer_price?.toFixed(2) || globalPricing.live_transfer_price.toFixed(2)} {clientSettings.using_custom_pricing && clientSettings.live_transfer_price_override && <span className="text-xs">(custom)</span>}</p>
                          <p>Data Lead: ${clientSettings.data_lead_price?.toFixed(2) || globalPricing.data_lead_price.toFixed(2)} {clientSettings.using_custom_pricing && clientSettings.data_lead_price_override && <span className="text-xs">(custom)</span>}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Balance & History Tab */}
          {activeTab === 'balance' && (
            <div className="space-y-6 max-w-4xl">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Current Balance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold text-green-600 font-mono">
                      ${client.balance?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Add Balance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Amount to Add</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={topUpAmount}
                          onChange={(e) => setTopUpAmount(e.target.value)}
                          className="pl-9"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Input
                        value={topUpNotes}
                        onChange={(e) => setTopUpNotes(e.target.value)}
                        placeholder="Payment reference, etc."
                      />
                    </div>
                    <Button 
                      onClick={handleAddBalance} 
                      disabled={addingBalance || !topUpAmount || parseFloat(topUpAmount) <= 0}
                      className="w-full"
                    >
                      {addingBalance ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <DollarSign className="w-4 h-4 mr-2" />
                      )}
                      Add ${topUpAmount || '0.00'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Transaction History
                  </CardTitle>
                  <CardDescription>
                    Recent balance changes and lead charges
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No transactions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.slice(0, 20).map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-mono font-medium ${tx.type === 'credit' || tx.type === 'refund' ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.type === 'credit' || tx.type === 'refund' ? '+' : '-'}${Math.abs(tx.amount).toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground">Balance: ${tx.balance_after?.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Admin Notes Tab */}
          {activeTab === 'notes' && (
            <div className="space-y-6 max-w-3xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Admin Notes
                  </CardTitle>
                  <CardDescription>
                    Internal notes about this client (not visible to the client)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Add any internal notes about this client, special arrangements, or other important information..."
                    value={clientSettings.admin_notes || ''}
                    onChange={(e) => setClientSettings({...clientSettings, admin_notes: e.target.value})}
                    className="min-h-[200px]"
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
